import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Building2, Users, ExternalLink, Workflow, HardHat, UserCog, FolderKanban
} from 'lucide-react';
import { clientAPI } from '../api';
import { locationLabel } from '../components/LocationPicker';
import { useAuth } from '../context/AuthContext';

const money = (v) => (v || v === 0 ? `₹${Number(v).toLocaleString('en-IN')}` : '—');
const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Stat = ({ label, value, cls = '', testid }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`font-semibold ${cls}`} data-testid={testid}>{value}</div>
  </div>
);

export default function ClientOverviewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const canOpenWorkflow = ['super_admin', 'manager', 'architect', 'architect_manager'].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    clientAPI.overview(clientId)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Failed to load client'); setData(null); })
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="clay-card p-6 text-sm text-muted-foreground" data-testid="client-loading">Loading client…</div>;
  if (!data) return <div className="clay-card p-6" data-testid="client-not-found">Client not found.</div>;

  const c = data.client;
  const cons = data.consolidated;

  return (
    <div className="space-y-4" data-testid="client-overview-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button onClick={() => navigate('/clients')} data-testid="back-to-clients"
            className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> All Clients
          </button>
          <h1 className="text-3xl font-outfit font-semibold sm:text-4xl" data-testid="client-name">{c.name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
              c.client_type === 'b2b' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30' : 'bg-slate-100 text-slate-600 dark:bg-slate-800'}`}
              data-testid="client-type-badge">
              {c.client_type === 'b2b' ? <Building2 className="h-3 w-3" /> : <Users className="h-3 w-3" />}
              {(c.client_type || 'b2c').toUpperCase()}
            </span>
            {c.company_name && <span>{c.company_name}</span>}
            {c.primary_mobile && <span>{c.primary_mobile}</span>}
            {(locationLabel(c) || c.city) && <span data-testid="client-location">{locationLabel(c) || c.city}</span>}
          </p>
          {c.b2b_partner_name && (
            <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300" data-testid="client-partner">
              Referred by partner: {c.b2b_partner_name}{c.b2b_partner_mobile ? ` · ${c.b2b_partner_mobile}` : ''}
            </p>
          )}
        </div>
        {data.can_add_project && (
          <button onClick={() => navigate(`/projects/new?client=${clientId}`)} data-testid="add-new-project-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white">
            <Plus className="h-4 w-4" /> Add New Project
          </button>
        )}
      </div>

      {cons && (
        <div className="clay-card grid grid-cols-2 gap-4 p-5 text-sm md:grid-cols-4" data-testid="client-consolidated">
          <Stat label="Projects" value={data.project_count} testid="cons-projects" />
          <Stat label="Total Contract Value" value={money(cons.contract_value)} testid="cons-contract" />
          <Stat label="Total Received" value={money(cons.received)} cls="text-green-600" testid="cons-received" />
          <Stat label="Total Pending" value={money(cons.pending)} cls="text-orange-600" testid="cons-pending" />
          <p className="col-span-2 text-[11px] text-muted-foreground md:col-span-4">
            Consolidated figures are the sum of this client's individual project ledgers — there is no client-level ledger or stored total.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium md:text-lg">Projects</h2>
        <span className="text-xs text-muted-foreground" data-testid="project-count">{data.project_count} independent project{data.project_count === 1 ? '' : 's'}</span>
      </div>

      {data.projects.length === 0 ? (
        <div className="clay-card p-8 text-center text-sm text-muted-foreground" data-testid="client-projects-empty">
          No projects yet for this client. Use <span className="font-medium">Add New Project</span> to create the first one.
        </div>
      ) : (
        <div className="space-y-3" data-testid="client-projects">
          {data.projects.map((p) => (
            <div key={p.project_id} data-testid={`project-row-${p.project_id}`}
              className="clay-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold" data-testid={`project-serial-${p.project_id}`}>{p.project_serial || '—'}</span>
                    {p.legacy && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800" data-testid={`project-legacy-${p.project_id}`}>Legacy record</span>}
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30" data-testid={`project-status-${p.project_id}`}>
                      {titleCase(p.project_status)}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate font-medium">{p.project_name || '—'}</p>
                  <p className="text-xs text-muted-foreground" data-testid={`project-service-${p.project_id}`}>
                    {p.service || (p.services_required || []).join(', ') || '—'}{p.city ? ` · ${p.city}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/projects/${p.project_id}`} data-testid={`open-project-${p.project_id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
                    <ExternalLink className="h-3.5 w-3.5" /> Open Project
                  </Link>
                  {canOpenWorkflow && p.assigned_architect_id && (
                    <Link to={`/architect/projects/${p.project_id}`} data-testid={`open-workflow-${p.project_id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/30">
                      <Workflow className="h-3.5 w-3.5" /> Open Workflow
                    </Link>
                  )}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-sm dark:border-slate-800 md:grid-cols-5">
                {data.can_view_finance ? (
                  <>
                    <Stat label="Contract Value" value={money(p.contract_value)} testid={`project-contract-${p.project_id}`} />
                    <Stat label="Received" value={money(p.received)} cls="text-green-600" testid={`project-received-${p.project_id}`} />
                    <Stat label="Pending" value={money(p.pending)} cls="text-orange-600" testid={`project-pending-${p.project_id}`} />
                  </>
                ) : (
                  <div className="col-span-2 text-xs text-muted-foreground md:col-span-3" data-testid={`project-finance-hidden-${p.project_id}`}>
                    Financial details need payment permissions
                  </div>
                )}
                <div>
                  <div className="text-xs text-muted-foreground">Architect</div>
                  <div className="flex items-center gap-1 text-sm" data-testid={`project-architect-${p.project_id}`}>
                    <HardHat className="h-3.5 w-3.5 text-muted-foreground" />{p.assigned_architect_name || 'Not Assigned'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Architect Manager</div>
                  <div className="flex items-center gap-1 text-sm" data-testid={`project-arch-manager-${p.project_id}`}>
                    <UserCog className="h-3.5 w-3.5 text-muted-foreground" />{p.architect_manager_name || 'Not Assigned'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.leads.length > 0 && (
        <div className="clay-card p-4" data-testid="client-leads">
          <h2 className="mb-2 text-base font-medium md:text-lg">Linked Leads</h2>
          <div className="flex flex-wrap gap-2">
            {data.leads.map((l) => (
              <Link key={l.lead_id} to={`/leads/${l.lead_id}`} data-testid={`client-lead-${l.lead_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
                <FolderKanban className="h-3 w-3" />{l.lead_no || l.customer_name} · {titleCase(l.status)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
